import { Building2 } from "lucide-react";
import { useState } from "react";

interface ClientWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete: (clientData: ClientFormData) => void;
}

interface ClientFormData {
  name: string;
  shortName?: string;
  notes?: string;
}

// Validation constants
const MAX_NAME_LENGTH = 100;
const MAX_NOTES_LENGTH = 500;

/**
 * Client Creation Wizard
 *
 * Philosophy: Client creation is a deliberate "mode transition" - moving from
 * exploring Nexus via the Demo Organization to managing your own organization.
 * This form is intentionally minimal. We don't ask for much because:
 *
 * 1. You've already seen what Nexus can do via the Demo
 * 2. Contact info and other details can be added later
 * 3. We want the transition to feel light, not bureaucratic
 *
 * The only required field is the organization name. Everything else can wait.
 */
const ClientWizard: React.FC<ClientWizardProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    shortName: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open) return null;

  const updateFormData = (updates: Partial<ClientFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const isValid = formData.name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // onComplete may or may not be async - handle both cases
      const result = onComplete(formData) as void | Promise<void>;
      if (result && typeof result === "object" && "then" in result) {
        await result;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid && !isSubmitting) {
      void handleSubmit();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-[var(--ws-surface)] border border-[var(--ws-border-soft)] rounded-xl shadow-xl"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[var(--ws-border-soft)]">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--ws-accent-soft)]">
              <Building2 className="w-5 h-5 text-[var(--ws-accent)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--ws-text)]">
                Create Organization
              </h2>
              <p className="text-sm text-[var(--ws-text-secondary)]">
                Start managing a new client
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Organization Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--ws-text)] mb-1.5">
              Organization Name{" "}
              <span className="text-[var(--ws-status-danger)]">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              placeholder="e.g., Dallas LIFE Foundation"
              maxLength={MAX_NAME_LENGTH}
              autoFocus
              className="w-full h-10 px-3 rounded-lg border bg-[var(--ws-surface)] text-sm text-[var(--ws-text)] placeholder:text-[var(--ws-text-tertiary)] border-[var(--ws-border-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--ws-accent)] focus:border-transparent transition-colors"
            />
            <div className="mt-1 flex justify-between">
              <span className="text-xs text-[var(--ws-text-secondary)]">
                This is the only required field
              </span>
              <span className="text-xs text-[var(--ws-text-tertiary)]">
                {formData.name.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
          </div>

          {/* Short Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--ws-text)] mb-1.5">
              Short Name{" "}
              <span className="text-xs font-normal text-[var(--ws-text-secondary)]">
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={formData.shortName}
              onChange={(e) => updateFormData({ shortName: e.target.value })}
              placeholder="e.g., DLIFE"
              className="w-full h-10 px-3 rounded-lg border bg-[var(--ws-surface)] text-sm text-[var(--ws-text)] placeholder:text-[var(--ws-text-tertiary)] border-[var(--ws-border-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--ws-accent)] focus:border-transparent transition-colors"
            />
            <p className="mt-1 text-xs text-[var(--ws-text-secondary)]">
              Used in navigation and reports
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[var(--ws-text)] mb-1.5">
              Notes{" "}
              <span className="text-xs font-normal text-[var(--ws-text-secondary)]">
                (optional)
              </span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateFormData({ notes: e.target.value })}
              placeholder="Any context you want to remember..."
              maxLength={MAX_NOTES_LENGTH}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border bg-[var(--ws-surface)] text-sm text-[var(--ws-text)] placeholder:text-[var(--ws-text-tertiary)] border-[var(--ws-border-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--ws-accent)] focus:border-transparent resize-none transition-colors"
            />
          </div>

          {/* Helper Text */}
          <div className="pt-2 border-t border-[var(--ws-border-soft)]">
            <p className="text-xs text-[var(--ws-text-secondary)] leading-relaxed">
              You can add contact information, branding, and other details after
              the organization is created. Start simple.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--ws-border-soft)] bg-[var(--ws-surface-subtle)] rounded-b-xl flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--ws-text-secondary)] hover:text-[var(--ws-text)] transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={() => void handleSubmit()}
            disabled={!isValid || isSubmitting}
            className="inline-flex items-center px-5 py-2 rounded-lg text-sm font-medium text-white bg-[var(--ws-accent)] hover:bg-[var(--ws-accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSubmitting ? "Creating..." : "Create Organization"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientWizard;
